import { Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'services/auth';

const useStyles = makeStyles((theme: any) => ({
  shell: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  banner: {
    width: '100%',
    maxWidth: theme.spacing(60),
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    borderTop: `${theme.spacing(0.5)} solid ${theme.palette.warning.main}`,
  },
  message: {
    color: theme.palette.text.primary,
  },
  error: {
    color: theme.palette.error.dark,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

// Shown when the session cannot be refreshed (network/server). Mounts no authenticated providers.
const DegradedBanner = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { retrySession } = useAuth();
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const mounted = useRef(true);

  // Re-arm on every mount: StrictMode's dev remount runs the cleanup once before the real mount.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const handleRetry = async () => {
    setIsRetrying(true);
    setHasFailed(false);
    const status = await retrySession();
    // On success (or anonymous) the route guard swaps this banner out; only a still-degraded result stays here.
    if (!mounted.current) return;
    setIsRetrying(false);
    setHasFailed(status !== 'authenticated');
  };

  return (
    <div className={classes.shell}>
      <Paper className={classes.banner} elevation={0} role="region" aria-live="polite" aria-busy={isRetrying}>
        <Typography variant="body1" className={classes.message}>
          {t('auth:degraded-banner')}
        </Typography>
        {hasFailed && (
          <Typography variant="body2" role="alert" className={classes.error}>
            {t('common:error-try-again')}
          </Typography>
        )}
        <div className={classes.actions}>
          <ContainedButton
            text={t('common:retry')}
            ariaLabel={t('common:retry')}
            icon="sync"
            isLoading={isRetrying}
            disabled={isRetrying}
            onClick={handleRetry}
          />
        </div>
      </Paper>
    </div>
  );
};

export default DegradedBanner;
